/**
 * CR140: Hook for fetching schedule run history
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { BatchRunScheduleRun } from '@/lib/scheduler/types';

export function useScheduleRuns(scheduleId: string | null) {
  const [runs, setRuns] = useState<BatchRunScheduleRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchRuns = useCallback(async () => {
    if (!scheduleId) {
      setRuns([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('batch_run_schedule_runs')
        .select('*')
        .eq('schedule_id', scheduleId)
        .order('scheduled_for', { ascending: false })
        .limit(50);

      if (fetchError) throw fetchError;

      setRuns((data || []) as BatchRunScheduleRun[]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch schedule runs';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [scheduleId, toast]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  return {
    runs,
    loading,
    error,
    refetch: fetchRuns,
  };
}
